import { z } from "zod";

export const RuleEventSchema = z.enum([
  "onSave",
  "onBOQRowAdded",
  "onSkuChanged",
  "onMilestoneSet",
  "onApprovalDecided",
  "onBudgetChanged",
  "onDeadlineChanged",
]);
export type RuleEvent = z.infer<typeof RuleEventSchema>;

export const ComparatorSchema = z.enum([">", ">=", "<", "<=", "==", "!="]);
export type Comparator = z.infer<typeof ComparatorSchema>;

export const LiteralSchema = z.object({
  kind: z.literal("lit"),
  type: z.enum(["number", "string", "boolean"]),
  value: z.union([z.number(), z.string(), z.boolean()]),
  unit: z.string().optional(),
});
export type Literal = z.infer<typeof LiteralSchema>;

export const FieldRefSchema = z.object({
  kind: z.literal("field"),
  path: z.string(),
  type: z.enum(["number", "string", "date", "vector", "boolean"]),
  unit: z.string().optional(),
});
export type FieldRef = z.infer<typeof FieldRefSchema>;

export const TermSchema = z.union([LiteralSchema, FieldRefSchema]);
export type Term = z.infer<typeof TermSchema>;

/* Node-pattern term — names a typed graph node with optional typed
 * attribute filters (e.g. Room where roomKind="bath").
 * Per CLAUDE.md §1 + user direction: rules are 100% ontology-typed.
 * The AST IS the SPARQL we'd materialise. No string predicates. */
export const NodePatternSchema = z.object({
  kind: z.literal("pattern"),
  /** NodeKind from types/ontology.ts (Room, SKU, Project, Placement, …) */
  nodeKind: z.string(),
  /** Optional `attr == value` filter, e.g. roomKind="bath", kind="PoojaAltar". */
  filterAttr: z.string().optional(),
  filterValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
  /** Optional binding name so multiple patterns can refer back. */
  bind: z.string().optional(),
});
export type NodePattern = z.infer<typeof NodePatternSchema>;

/** Aggregation function over a typed set. */
export const AggOpSchema = z.enum(["sum", "avg", "count", "min", "max"]);
export type AggOp = z.infer<typeof AggOpSchema>;

/** Aggregation term — Σ / avg / count / min / max of a FieldRef over
 *  a filtered subject set. E.g. Σ BudgetLine.amount where FOR_ROOM=Kitchen. */
export const AggTermSchema = z.object({
  kind: z.literal("agg"),
  op: AggOpSchema,
  /** Subject set this aggregation walks. */
  over: NodePatternSchema,
  /** What numeric field to aggregate. Omit for `count`. */
  field: FieldRefSchema.optional(),
  /** Optional relation that the subject must travel before counting
   *  (e.g. `BudgetLine FOR_ROOM Room(kind=Kitchen)` aggregates only
   *   budget lines that touch a kitchen room). */
  via: z.object({ relation: z.string(), target: NodePatternSchema }).optional(),
  /** Distinct counting — for `count(distinct Material in Room)`. */
  distinct: z.boolean().optional(),
});
export type AggTerm = z.infer<typeof AggTermSchema>;

/** Set literal — closed enum of strings (or numbers) used by
 *  set-membership predicates: `Placement.facing ∉ {E, NE}`. */
export const SetLiteralSchema = z.object({
  kind: z.literal("set"),
  type: z.enum(["string", "number"]),
  values: z.array(z.union([z.string(), z.number()])).min(1),
});
export type SetLiteral = z.infer<typeof SetLiteralSchema>;

/** RangeLiteral — closed interval [lo, hi]. Used with `in`/`not_in`. */
export const RangeLiteralSchema = z.object({
  kind: z.literal("range"),
  type: z.enum(["number"]),
  lo: z.number(),
  hi: z.number(),
  unit: z.string().optional(),
});
export type RangeLiteral = z.infer<typeof RangeLiteralSchema>;

/* Predicate is a discriminated union — typed without `any`. Every
 * rule in the system is one of these. The natural-language sentence
 * shown in the UI is *rendered from* the AST, never the other way.
 *
 *   cmp           — scalar comparison: lhs ⟨op⟩ rhs
 *                   Both sides are Term | AggTerm (so Σ BudgetLine
 *                   on the lhs and `Project.budgetCap × 0.25` on the
 *                   rhs both work).
 *   in_range      — value ∈ [lo, hi]
 *   not_in_range  — value ∉ [lo, hi]
 *   in_set        — value ∈ {a, b, c}
 *   not_in_set    — value ∉ {a, b, c}
 *   graph_pattern — subject ⟶ relation ⟶ object triple.
 *   exists        — "at least one S P O exists"
 *   forbidden     — "no S P O may exist" (the cultural/spatial NOTs)
 *   and / or / not — boolean composition
 */
export const ExtendedTermSchema = z.union([LiteralSchema, FieldRefSchema, AggTermSchema]);
export type ExtendedTerm = z.infer<typeof ExtendedTermSchema>;

/**
 * Spatial predicate kinds (B.1 / Seam 6).
 *
 *  clearance     — every (subjectKind ∈ room) must have ≥ minM clearance
 *                  on all sides (or only `directions`) to the nearest wall
 *                  AND to any sibling SKU in the same room. NKBA/NBC.
 *  sightline     — from a `from` SKU to a `to` SKU (or room landmark),
 *                  no opaque obstacle within the line segment. ANSI A117.1
 *                  reach + visibility.
 *  work_triangle — sum of the (sink|stove|fridge) leg distances must
 *                  satisfy minM/maxM. NKBA Guideline 5.
 *  egress_path   — every SKU labelled `door` or `egress` must have a
 *                  ≥ minM corridor to the nearest other egress / room exit.
 *                  IBC, NBC Part 4 §3.
 *
 * All four ride on Seam 6 (the Geometric Predicate Layer) — turf.js
 * evaluator client-side, shapely mirror server-side. They emit the
 * same RULE_FIRED_FROM / RULE_BLOCKED_BY edges as the scalar kinds,
 * so the Why-engine traces them identically.
 */
export type SpatialDirection = "north" | "south" | "east" | "west" | "any";

export type Predicate =
  | { kind: "cmp"; lhs: ExtendedTerm; op: Comparator; rhs: ExtendedTerm }
  | { kind: "in_range"; value: ExtendedTerm; range: RangeLiteral }
  | { kind: "not_in_range"; value: ExtendedTerm; range: RangeLiteral }
  | { kind: "in_set"; value: ExtendedTerm; set: SetLiteral }
  | { kind: "not_in_set"; value: ExtendedTerm; set: SetLiteral }
  | { kind: "graph_pattern"; subject: NodePattern; relation: string; object: NodePattern | Literal }
  | { kind: "exists"; pattern: { subject: NodePattern; relation: string; object: NodePattern | Literal } }
  | { kind: "forbidden"; pattern: { subject: NodePattern; relation: string; object: NodePattern | Literal } }
  | { kind: "clearance"; subjectKind: string; roomKind?: string | undefined; minM: number; directions?: ReadonlyArray<SpatialDirection> | undefined }
  | { kind: "sightline"; fromKind: string; toKind: string; roomKind?: string | undefined; obstacleKinds?: ReadonlyArray<string> | undefined }
  | { kind: "work_triangle"; roomKind: string; legs: ReadonlyArray<string>; minM: number; maxM: number }
  | { kind: "egress_path"; subjectKind: string; minWidthM: number; maxDistanceM: number }
  | { kind: "and"; left: Predicate; right: Predicate }
  | { kind: "or"; left: Predicate; right: Predicate }
  | { kind: "not"; inner: Predicate };

export const PredicateSchema: z.ZodType<Predicate> = z.lazy(() =>
  z.union([
    z.object({ kind: z.literal("cmp"), lhs: ExtendedTermSchema, op: ComparatorSchema, rhs: ExtendedTermSchema }),
    z.object({ kind: z.literal("in_range"),     value: ExtendedTermSchema, range: RangeLiteralSchema }),
    z.object({ kind: z.literal("not_in_range"), value: ExtendedTermSchema, range: RangeLiteralSchema }),
    z.object({ kind: z.literal("in_set"),       value: ExtendedTermSchema, set: SetLiteralSchema }),
    z.object({ kind: z.literal("not_in_set"),   value: ExtendedTermSchema, set: SetLiteralSchema }),
    z.object({
      kind: z.literal("graph_pattern"),
      subject: NodePatternSchema, relation: z.string(),
      object: z.union([NodePatternSchema, LiteralSchema]),
    }),
    z.object({
      kind: z.literal("exists"),
      pattern: z.object({ subject: NodePatternSchema, relation: z.string(), object: z.union([NodePatternSchema, LiteralSchema]) }),
    }),
    z.object({
      kind: z.literal("forbidden"),
      pattern: z.object({ subject: NodePatternSchema, relation: z.string(), object: z.union([NodePatternSchema, LiteralSchema]) }),
    }),
    z.object({
      kind: z.literal("clearance"),
      subjectKind: z.string(),
      roomKind: z.string().optional(),
      minM: z.number().nonnegative(),
      directions: z.array(z.enum(["north", "south", "east", "west", "any"])).optional(),
    }),
    z.object({
      kind: z.literal("sightline"),
      fromKind: z.string(),
      toKind: z.string(),
      roomKind: z.string().optional(),
      obstacleKinds: z.array(z.string()).optional(),
    }),
    z.object({
      kind: z.literal("work_triangle"),
      roomKind: z.string(),
      legs: z.array(z.string()).min(2),
      minM: z.number().nonnegative(),
      maxM: z.number().positive(),
    }),
    z.object({
      kind: z.literal("egress_path"),
      subjectKind: z.string(),
      minWidthM: z.number().positive(),
      maxDistanceM: z.number().positive(),
    }),
    z.object({ kind: z.literal("and"), left: PredicateSchema, right: PredicateSchema }),
    z.object({ kind: z.literal("or"),  left: PredicateSchema, right: PredicateSchema }),
    z.object({ kind: z.literal("not"), inner: PredicateSchema }),
  ]),
);

export const ActionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("flag"), severity: z.enum(["info", "warn", "block"]) }),
  z.object({ kind: z.literal("suggest"), query: z.string() }),
  z.object({ kind: z.literal("openApproval"), role: z.enum(["designer", "client", "vendor", "pm"]) }),
  z.object({
    kind: z.literal("substitute"),
    criteria: z.object({
      maxLeadDays: z.number().optional(),
      minStyleMatch: z.number().min(0).max(1).optional(),
      maxPriceDeltaPct: z.number().optional(),
    }),
  }),
]);
export type Action = z.infer<typeof ActionSchema>;

export const RuleScopeSchema = z.enum(["project", "studio"]);
export type RuleScope = z.infer<typeof RuleScopeSchema>;

export const RuleSchema = z.object({
  id: z.string(),
  name: z.string(),
  scope: RuleScopeSchema,
  when: RuleEventSchema,
  if: PredicateSchema,
  then: ActionSchema,
  because: z.string(),
  else: z
    .object({ action: ActionSchema, because: z.string() })
    .optional(),
  metrics: z.object({
    firedCount: z.number().int().nonnegative().default(0),
    acceptedCount: z.number().int().nonnegative().default(0),
    rejectedCount: z.number().int().nonnegative().default(0),
  }),
  createdAt: z.string(),
  promotedFrom: z.string().optional(),
});
export type Rule = z.infer<typeof RuleSchema>;

// A rule node on the canvas — purely UI state, but typed.
export const RuleNodeKindSchema = z.enum(["WHEN", "IF", "THEN", "BECAUSE", "ELSE"]);
export type RuleNodeKind = z.infer<typeof RuleNodeKindSchema>;

export interface CanvasNode {
  id: string;
  kind: RuleNodeKind;
  position: [number, number];
  data:
    | { kind: "WHEN"; event: RuleEvent }
    | { kind: "IF"; predicate: Predicate }
    | { kind: "THEN"; action: Action }
    | { kind: "BECAUSE"; template: string }
    | { kind: "ELSE"; action: Action; template: string };
}
